import { useEffect, useRef, useCallback, useState } from "react"
import { useOddsStore } from "../stores/oddsStore"
import { useScoresStore } from "../stores/scoresStore"

// ============================================================================
// EDGE CONFIGURATION - Added 2025-12-20
// ============================================================================
const EDGE_URL = "https://abet-edge.abetco.workers.dev"
const EDGE_WS_URL = "wss://abet-edge.abetco.workers.dev/ws"

// Check localStorage for Edge preference (persists across refreshes)
// DEFAULT: Edge is ON unless explicitly disabled
const getUseEdge = () => {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  // 2025-12-21: Default to LOCAL for localhost, EDGE for production
  if (host === 'localhost' || host === '127.0.0.1') {
    const stored = localStorage.getItem('abet_use_edge')
    return stored === 'true'  // Default OFF for localhost
  }
  const stored = localStorage.getItem('abet_use_edge')
  return stored !== 'false'  // Default ON for production
}

// Toggle Edge mode
export const setUseEdge = (useEdge) => {
  localStorage.setItem('abet_use_edge', useEdge ? 'true' : 'false')
  console.log(`[Config] Edge mode ${useEdge ? 'ENABLED' : 'DISABLED'}. Reload page to apply.`)
}

export const isEdgeEnabled = () => getUseEdge()

// ============================================================================
// URL CONFIGURATION
// ============================================================================

// Dynamically determine WebSocket URL based on environment and Edge setting
// Last Modified: 2025-12-20 - Added Edge support
const getWsUrl = () => {
  // Check if Edge mode is enabled
  if (getUseEdge()) {
    console.log("[WS] Using EDGE WebSocket")
    return EDGE_WS_URL
  }

  const host = window.location.hostname
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"

  if (host === "localhost" || host === "127.0.0.1") {
    return "ws://localhost:8443/ws?skip_init=true"
  }
  return `${protocol}//${window.location.host}/ws?skip_init=true`
}

const WS_URL = getWsUrl()
console.log("[WS] WebSocket URL configured:", WS_URL)
console.log("[WS] Edge mode:", getUseEdge() ? "ENABLED" : "DISABLED")

// HYBRID LOAD: Get HTTP API base URL for initial data fetches
const getApiBaseUrl = () => {
  // Check if Edge mode is enabled
  if (getUseEdge()) {
    console.log("[HTTP] Using EDGE API")
    return EDGE_URL
  }

  const host = window.location.hostname
  if (host === "localhost" || host === "127.0.0.1") {
    return "http://localhost:8443"
  }
  return ""
}

const API_BASE = getApiBaseUrl()

// ============================================================================
// EXPORTED FUNCTIONS
// ============================================================================

export async function fetchLeagueOdds(league) {
  if (!league) return []
  // Note: /api/odds endpoint returns ALL leagues, so we filter client-side
  // The /api/odds/:league endpoint has caching issues on the receiver
  const url = `${API_BASE}/api/odds`
  const source = getUseEdge() ? "EDGE" : "VM"
  console.log(`[HTTP:${source}] Fetching odds for ${league}`)
  try {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const allData = await res.json()
    // Filter to requested league (case-insensitive)
    const leagueUpper = league.toUpperCase()
    const data = allData.filter(item =>
      (item.league || '').toUpperCase() === leagueUpper
    )
    console.log(`[HTTP:${source}] Loaded ${data.length} ${league} outcomes (filtered from ${allData.length} total)`)
    return data
  } catch (err) {
    console.error(`[HTTP:${source}] Failed ${league}:`, err)
    return []
  }
}

// ============================================================================
// WEBSOCKET HOOK
// ============================================================================

const RECONNECT_DELAY = 3000
const MAX_RECONNECT_ATTEMPTS = 10

// FIXED 2025-12-19: let for reset on reconnect
let latencyHistory = []
const MAX_LATENCY_SAMPLES = 50
let messageCount = 0

export function useWebSocket() {
  const wsRef = useRef(null)
  const reconnectAttempts = useRef(0)
  const reconnectTimeout = useRef(null)

  const setConnected = useOddsStore((s) => s.setConnected)
  const updateOdds = useOddsStore((s) => s.updateOdds)
  const batchUpdateOdds = useOddsStore((s) => s.batchUpdateOdds)
  const incrementUpdateCount = useOddsStore((s) => s.incrementUpdateCount)
  const isConnected = useOddsStore((s) => s.isConnected)
  const batchUpdateScores = useScoresStore((s) => s.batchUpdateScores)

  const [latencyStats, setLatencyStats] = useState({
    current: null, avg: null, min: null, max: null, updatesPerSecond: 0, lastUpdateTime: null
  })
  const updateTimestamps = useRef([])

  const calculateUPS = useCallback(() => {
    const now = Date.now()
    updateTimestamps.current = updateTimestamps.current.filter(t => t > now - 1000)
    return updateTimestamps.current.length
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return
    if (wsRef.current) wsRef.current.close()

    const source = getUseEdge() ? "EDGE" : "VM"
    console.log(`[WS:${source}] Connecting to:`, WS_URL)
    try {
      wsRef.current = new WebSocket(WS_URL)

      wsRef.current.onopen = () => {
        // FIXED 2025-12-19: Reset latency history on reconnect to prevent stale averages
        latencyHistory = []
        console.log(`[WS:${source}] Connected!`)
        setConnected(true)
        reconnectAttempts.current = 0
      }

      wsRef.current.onmessage = (event) => {
        const receiveTime = performance.now()
        const receiveTimestamp = Date.now()
        try {
          const data = JSON.parse(event.data)
          if (data.server_timestamp) {
            // Handle both integer timestamps (Edge/localhost) and RFC3339 strings (VM)
            const serverTs = typeof data.server_timestamp === 'number'
              ? data.server_timestamp
              : new Date(data.server_timestamp).getTime()
            const latency = receiveTimestamp - serverTs
            latencyHistory.push(latency)
            if (latencyHistory.length > MAX_LATENCY_SAMPLES) latencyHistory.shift()
            const avg = Math.round(latencyHistory.reduce((a, b) => a + b, 0) / latencyHistory.length)
            updateTimestamps.current.push(receiveTimestamp)
            setLatencyStats({ current: latency, avg, min: Math.min(...latencyHistory), max: Math.max(...latencyHistory), updatesPerSecond: calculateUPS(), lastUpdateTime: new Date() })
          }
          messageCount++
          if (data.type === "sync" || data.type === "update" || data.type === "INIT" || data.type === "UPDATE") {
            const items = data.fixtures || data.odds || data.data || []
            if (Array.isArray(items) && items.length > 0) {
              batchUpdateOdds(items)
              incrementUpdateCount()
            }
          } else if (data.type === "SCORES_UPDATE" || data.type === "SCORES_INIT") {
            const scores = data.data || []
            if (Array.isArray(scores) && scores.length > 0) batchUpdateScores(scores)
          } else if (data.fixture_id || data.canonical_id) {
            updateOdds(data.fixture_id || data.canonical_id, data)
            incrementUpdateCount()
          }
        } catch (err) {
          console.error("[WS] Error:", err)
        }
      }

      wsRef.current.onerror = (err) => console.error("[WS] Error:", err)

      wsRef.current.onclose = (event) => {
        console.log("[WS] Disconnected", event.code)
        setConnected(false)
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current++
          reconnectTimeout.current = setTimeout(connect, RECONNECT_DELAY)
        }
      }
    } catch (err) {
      console.error("[WS] Connection error:", err)
      setConnected(false)
    }
  }, [setConnected, updateOdds, batchUpdateOdds, incrementUpdateCount, batchUpdateScores, calculateUPS])

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current)
    if (wsRef.current) wsRef.current.close()
    setConnected(false)
  }, [setConnected])

  useEffect(() => {
    connect()
    return () => disconnect()
  }, [connect, disconnect])

  return {
    isConnected,
    reconnect: connect,
    disconnect,
    latencyStats,
    wsUrl: WS_URL,
    isEdge: getUseEdge(),
    edgeUrl: EDGE_URL
  }
}
